import { motion } from "framer-motion";
import { Clock, ImageOff, PiggyBank } from "lucide-react";
import { useSiteImages } from "@/context/SiteImagesContext";
import { useSiteContent } from "@/context/SiteContentContext";

const CARDS = [
  { icon: Clock, title: "Save Hours", desc: "Eliminate hours of manual editing", color: "text-indigo-400" },
  { icon: ImageOff, title: "No Photoshop", desc: "Eliminate need for Photoshop expertise", color: "text-violet-400" },
  { icon: PiggyBank, title: "Reduce Costs", desc: "Reduce staffing costs and overhead", color: "text-fuchsia-400" },
];

const STATS = [
  { label: "30-second processing", dot: "bg-emerald-400" },
  { label: "40x faster than manual", dot: "bg-indigo-400" },
  { label: "No technical skills required", dot: "bg-fuchsia-400" },
];

export const Benefits = () => {
  const { img } = useSiteImages();
  const { t } = useSiteContent();
  return (
    <section id="benefits" className="relative py-28 px-6" data-testid="benefits-section">
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }} transition={{ duration: 0.6 }}
          className="text-center max-w-2xl mx-auto"
        >
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
            {t('benefits_title')} <span className="text-gradient">{t('benefits_highlight')}</span>
          </h2>
          <p className="mt-4 text-white/55 text-lg">{t('benefits_subtitle')}</p>
        </motion.div>

        <div className="mt-14 grid md:grid-cols-3 gap-5">
          {CARDS.map((c, i) => (
            <motion.div
              key={c.title}
              data-testid={`benefit-card-${i}`}
              initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.1 }}
              className="glass rounded-2xl p-8 hover:border-white/20 transition-colors duration-300"
            >
              <span className="grid place-items-center h-12 w-12 rounded-xl bg-white/5 mb-5">
                <c.icon className={`h-6 w-6 ${c.color}`} />
              </span>
              <h3 className="text-xl font-semibold mb-2">{c.title}</h3>
              <p className="text-white/55">{c.desc}</p>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.97 }} whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }} transition={{ duration: 0.6 }}
          className="mt-12 relative rounded-3xl overflow-hidden border border-white/10"
        >
          <img src={img('irisTeal')} alt="Iris editing preview" className="w-full h-[360px] sm:h-[460px] object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#050507] via-transparent to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 px-6 py-5 flex flex-wrap items-center justify-center gap-x-8 gap-y-3">
            {STATS.map((s) => (
              <span key={s.label} className="flex items-center gap-2 text-sm text-white/85" data-testid={`stat-${s.label.split(" ")[0].toLowerCase()}`}>
                <span className={`h-2 w-2 rounded-full ${s.dot}`} /> {s.label}
              </span>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};
